import {
  endOfWeek,
  format,
  formatDistanceToNow,
  formatISO,
  isValid,
  parseISO,
  startOfWeek,
  subWeeks,
} from 'date-fns';
import { enUS, ru } from 'date-fns/locale';

import { APP_CONFIG } from '@/config/app';
import i18n from '@/config/i18n';

// Locale mapping for date-fns
const locales = {
  en: enUS,
  ru: ru,
} as const;

/**
 * Get current date-fns locale based on i18n language
 */
export function getLocale() {
  const lang = i18n.language as keyof typeof locales;
  return locales[lang] ?? enUS;
}

export function getDateFnsLocale(language: string) {
  return locales[language as keyof typeof locales] ?? enUS;
}

/**
 * Format a date for display (locale-aware)
 */
export function formatDate(
  date: Date | string | null | undefined,
  formatString: string = APP_CONFIG.dateFormats.display
): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(dateObj)) return '';
  return format(dateObj, formatString, { locale: getLocale() });
}

/**
 * Format a date with time for display (locale-aware)
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  return formatDate(date, APP_CONFIG.dateFormats.displayWithTime);
}

/**
 * Format a date for API requests (not locale-aware - uses ISO format)
 */
export function formatDateForApi(date: Date | null | undefined): string {
  if (!date || !isValid(date)) return '';
  return formatISO(date);
}

/**
 * Format relative time (e.g., "2 hours ago") - locale-aware
 */
export function formatRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(dateObj)) return '';
  return formatDistanceToNow(dateObj, { addSuffix: true, locale: getLocale() });
}

/**
 * Get last week's date range (Monday to Sunday)
 */
export function getLastWeekRange(): { min: string; max: string } {
  const lastWeekStart = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
  const lastWeekEnd = endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
  return {
    min: formatISO(lastWeekStart),
    max: formatISO(lastWeekEnd),
  };
}

/**
 * Format time from seconds to HH:MM:SS
 */
export function formatTime(seconds: number): string {
  const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');
  return `${hrs}:${mins}:${secs}`;
}

/**
 * Parse time string (HH:MM:SS) to seconds
 */
export function parseTimeToSeconds(time: string): number {
  const parts = time.split(':').map(Number);
  if (parts.length === 3) {
    const [hrs, mins, secs] = parts;
    return (hrs ?? 0) * 3600 + (mins ?? 0) * 60 + (secs ?? 0);
  }
  if (parts.length === 2) {
    const [hrs, mins] = parts;
    return (hrs ?? 0) * 3600 + (mins ?? 0) * 60;
  }
  return 0;
}
